import React, { useEffect, useState } from "react";
import { ScrollArea, useToast, Spinner } from "../../components/ui";
import constant, { statuses } from "../../constants";
import { FaPlus, FaTimes, FaCheckCircle } from "react-icons/fa";
import { Header, UserPreview } from "../../components/modules";
import { DatePicker } from "antd";
import th from "antd/es/date-picker/locale/th_TH";
import dayjs from "dayjs";
import { IoMdTrash } from "react-icons/io";
import { FiCreditCard } from "react-icons/fi";
import Context from "../../Context";
import {
  RiCoupon2Line,
  RiFlashlightLine,
  RiTicketLine,
  RiTimeLine,
  RiCloseLine,
  RiEditLine,
} from "react-icons/ri";
import { FiSearch, FiExternalLink } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/modules/alertDialog";

export const CouponOrderInfo = () => {
  const { toast } = useToast();
  const { orderId } = useParams();
  const { callApi, navigate, profileState, configs } = Context();
  const [order, setOrder] = useState(false);
  const [user, setUser] = useState(false);
  const [reserveOrder, setReserveOrder] = useState(false);
  const [selectReserve, setSelectReserve] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [note, setNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);

  // Dev check: allow special dev-only testing features by role or environment
  const isDev =
    (profileState?.role || "").toLowerCase() === "dev" ||
    Boolean(profileState?.isDev) ||
    process.env.REACT_APP_EVIRONMENT !== "prod";

  // Price edit modal state
  const [editingPrice, setEditingPrice] = useState(false);
  const [newPrice, setNewPrice] = useState("");

  // Expire date edit state
  const [editingExpire, setEditingExpire] = useState(false);
  const [newExpireAt, setNewExpireAt] = useState(null);

  // Special Dev Delete Used Reserve Modal State (Option 3)
  const [deleteUsedModal, setDeleteUsedModal] = useState({
    open: false,
    resOrder: null,
    cancelOrder: true,
  });

  useEffect(() => {
    getOrder();
  }, [orderId]);

  const getOrder = async () => {
    setLoading(true);
    const { status, data } = await callApi(
      "coupons-getCouponOrder",
      { orderId },
      { notShowLoading: true }
    );
    if (status && data) {
      setOrder(data);
      const targetUserId = data?.userId || data?.userData?.userId;
      if (targetUserId) {
        getUser(targetUserId);
      }
    } else {
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> ไม่พบคำสั่งซื้อคูปอง
          </div>
        ),
      });
    }
    setLoading(false);
  };

  const getUser = async (userId) => {
    const { status, data } = await callApi(
      "users-getUserInfoAdmin",
      { userId },
      { notShowLoading: true }
    );
    if (status) {
      setUser(data);
    }
  };

  // Instant Auto-Match for this Coupon (or specific number)
  const handleAutoMatch = async (reserveNumber = null) => {
    if (!order?.id) return;
    setAutoMatchLoading(true);
    try {
      const res = await callApi(
        "orders-autoMatchReserveOrders",
        {
          roundDate: configs.roundDate,
          couponOrderId: order.id,
          ...(reserveNumber ? { reserveNumber } : {}),
        },
        { notShowLoading: true }
      );

      if (res?.status) {
        const matched = res.totalTicketsMatched || 0;
        const failed = res.totalFailed || 0;

        if (matched > 0) {
          toast({
            className: "bg-emerald-600 text-white font-semibold",
            title: (
              <div className="flex gap-2 items-center">
                <FaCheckCircle /> จัดสลากอัตโนมัติสำเร็จ {matched} ใบ ({res.totalSuccessOrders || 1} ออเดอร์)
              </div>
            ),
          });
        } else if (failed > 0) {
          toast({
            className: "bg-amber-600 text-white font-semibold",
            title: (
              <div className="flex gap-2 items-center">
                <FaTimes /> สลากในคลังไม่พอสำหรับ {res.failedList?.map((f) => f.number).join(", ")}
              </div>
            ),
          });
        } else {
          toast({
            className: "bg-blue text-white font-semibold",
            title: (
              <div className="flex gap-2 items-center">
                <FaCheckCircle /> {res.message || "ประมวลผลเรียบร้อย"}
              </div>
            ),
          });
        }
        await getOrder();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaTimes /> {res?.message || "ไม่สามารถจัดสลากอัตโนมัติได้"}
            </div>
          ),
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เกิดข้อผิดพลาดในระบบ
          </div>
        ),
      });
    }
    setAutoMatchLoading(false);
  };

  // Status Change Handler (Direct with Panel Loading)
  const handleChangeOrderStatus = async (newStatus) => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const { status, message } = await callApi(
        "coupons-editCouponOrder",
        { id: order.id, key: "status", value: newStatus },
        { notShowLoading: true }
      );
      if (status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaCheckCircle /> อัปเดตสถานะสำเร็จ
            </div>
          ),
        });
        await getOrder();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaTimes /> {message || "อัปเดตสถานะไม่สำเร็จ"}
            </div>
          ),
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เกิดข้อผิดพลาดในการอัปเดตสถานะ
          </div>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  // Coupon Status Change Handler (Direct with Panel Loading)
  const handleChangeCouponStatus = async (newStatus) => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const { status } = await callApi(
        "coupons-editCouponOrder",
        { id: order.id, key: "coupon.status", value: newStatus },
        { notShowLoading: true }
      );
      if (status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaCheckCircle /> เปลี่ยนสถานะคูปองสำเร็จ
            </div>
          ),
        });
        await getOrder();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaTimes /> อัปเดตสถานะคูปองไม่สำเร็จ
            </div>
          ),
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เกิดข้อผิดพลาดในการเปลี่ยนสถานะคูปอง
          </div>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  // Save Expire Date Change (Direct with Panel Loading)
  const handleSaveExpireAt = async () => {
    if (!newExpireAt || !order?.id) return;
    const expireTimestamp = newExpireAt.valueOf();
    setLoading(true);
    try {
      const { status, message } = await callApi(
        "coupons-editCouponOrder",
        { id: order.id, key: "coupon.expireAt", value: expireTimestamp },
        { notShowLoading: true }
      );
      if (status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaCheckCircle /> อัปเดตวันหมดอายุสำเร็จ
            </div>
          ),
        });
        setEditingExpire(false);
        await getOrder();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaTimes /> {message || "อัปเดตวันหมดอายุไม่สำเร็จ"}
            </div>
          ),
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เกิดข้อผิดพลาดในการอัปเดตวันหมดอายุ
          </div>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  // Save Price Change (Direct with Panel Loading)
  const handleSavePrice = async () => {
    const numPrice = Number(newPrice);
    if (isNaN(numPrice) || numPrice < 0) return;
    setLoading(true);
    try {
      const { status } = await callApi(
        "coupons-editCouponOrder",
        { id: order?.id, key: "price", value: numPrice },
        { notShowLoading: true }
      );
      if (status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaCheckCircle /> อัปเดตราคาสำเร็จ
            </div>
          ),
        });
        setEditingPrice(false);
        await getOrder();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaTimes /> อัปเดตราคาไม่สำเร็จ
            </div>
          ),
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เกิดข้อผิดพลาดในการปรับราคา
          </div>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  // Remove Pending Reserve Order (Direct with Panel Loading)
  const handleRemoveReserveOrder = async (resOrder) => {
    if (!order?.id || !resOrder) return;
    setLoading(true);
    try {
      const { status, message } = await callApi(
        "coupons-removeReserveOrder",
        { id: order.id, reserveOrder: resOrder },
        { notShowLoading: true }
      );
      if (status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaCheckCircle /> ลบจองสิทธิ์สำเร็จ
            </div>
          ),
        });
        await getOrder();
        setReserveOrder(false);
        if (selectReserve?.number === resOrder.number) {
          setSelectReserve(false);
        }
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaTimes /> {message || "ลบจองสิทธิ์ไม่สำเร็จ"}
            </div>
          ),
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เกิดข้อผิดพลาดในการลบสิทธิ์
          </div>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  // Option 3: Handle Deleting Already Used Reserve Order (DEV Only)
  const handleExecuteDeleteUsed = async () => {
    const resOrder = deleteUsedModal.resOrder;
    if (!resOrder) return;
    setLoading(true);
    try {
      // 1. If checkbox is checked, cancel the associated order first to restore tickets
      if (deleteUsedModal.cancelOrder && resOrder.orderId) {
        await callApi(
          "orders-edit",
          {
            id: resOrder.orderId,
            key: "status",
            value: statuses.CANCEL,
          },
          { notShowLoading: true }
        );
      }

      // 2. Remove reserve order from coupon
      const { status, message } = await callApi(
        "coupons-removeReserveOrder",
        { id: order.id, reserveOrder: resOrder },
        { notShowLoading: true }
      );

      if (status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaCheckCircle /> ลบสิทธิ์จอง {deleteUsedModal.cancelOrder ? "และยกเลิกออเดอร์คืนสลาก" : ""} เรียบร้อย
            </div>
          ),
        });
        setDeleteUsedModal({ open: false, resOrder: null, cancelOrder: true });
        getOrder();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: (
            <div className="flex gap-2 items-center">
              <FaTimes /> {message || "ไม่สามารถลบสิทธิ์จองที่จัดแล้วได้"}
            </div>
          ),
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เกิดข้อผิดพลาดในการลบสิทธิ์
          </div>
        ),
      });
    }
    setLoading(false);
  };

  // Add Note Handler
  const handleAddNote = async () => {
    if (!note.trim()) return;
    setNoteLoading(true);
    const { status } = await callApi(
      "coupons-editCouponOrder",
      { id: order.id, key: "note", value: note.trim() },
      { notShowLoading: true }
    );
    if (status) {
      toast({
        className: "bg-emerald-600 text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaCheckCircle /> เพิ่มโน้ตสำเร็จ
          </div>
        ),
      });
      setNote("");
      getOrder();
    } else {
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> เพิ่มโน้ตไม่สำเร็จ
          </div>
        ),
      });
    }
    setNoteLoading(false);
  };

  // Pending reserves count in this coupon
  const pendingReserves = (order?.coupon?.reserveOrder || []).filter(
    (r) => r.status === statuses.PENDING || !r.status
  );

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-20 px-3">
      {/* ── Top Breadcrumb & Navigation Bar ─────────────────────────────── */}
      <div className="flex items-center justify-between mt-2 mb-4">
        <Header
          header="Coupons"
          subHeader={`คำสั่งซื้อคูปอง #${order?.shortId || (orderId ? orderId.slice(-5).toUpperCase() : "")}`}
          icon={<RiCoupon2Line className="text-blue" />}
        />
        {/* <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition shadow-2xs"
        >
          ย้อนกลับ
        </button> */}
      </div>

      {!order ? (
        <div className="flex flex-col justify-center items-center h-80 w-full bg-white rounded-md border border-slate-200 shadow-sm gap-3">
          <Spinner className="w-8 h-8 text-blue" />
          <p className="text-sm font-semibold text-slate-500">กำลังโหลดข้อมูลคำสั่งซื้อ...</p>
        </div>
      ) : (
        <div className="relative flex flex-col gap-6">
          {/* Panel Loading Overlay (Fade White Screen with Locked Panel) */}
          {loading && (
            <div className="absolute -inset-3 z-40 bg-white/70 backdrop-blur-[1px] rounded-md flex flex-col items-center justify-center gap-3 animate-in fade-in duration-150">
              <div className="flex flex-col items-center gap-2.5 p-4 bg-white/95 border border-slate-200 rounded-lg shadow-md">
                <Spinner className="w-7 h-7 text-blue" />
                <span className="text-xs font-bold text-slate-700">กำลังอัปเดตข้อมูล...</span>
              </div>
            </div>
          )}

          {/* ═════════════════════════════════════════════════════════════════ */}
          {/* TOP GRID: LEFT (Col 8 - Order Info & Reserves) | RIGHT (Col 4 - Slip & Notes) */}
          {/* ═════════════════════════════════════════════════════════════════ */}
          <div className={`grid grid-cols-12 gap-5 items-start ${loading ? "pointer-events-none select-none" : ""}`}>
            {/* ───────────────────────────────────────────────────────────── */}
            {/* LEFT COLUMN (Col 8): Order Info & Reserve Rights Table        */}
            {/* ───────────────────────────────────────────────────────────── */}
            <div className="col-span-12 lg:col-span-8 flex flex-col gap-5">
              {/* Card 1: Order Information */}
              <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/60">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center text-blue font-bold">
                      <RiCoupon2Line className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-slate-800">
                        คำสั่งซื้อคูปอง #{order.shortId}
                      </h2>
                      <p className="text-xs text-slate-500 font-medium">
                        สร้างเมื่อ: {order.createdAt ? new Date(order.createdAt).toLocaleDateString("th-TH", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }) : "-"}
                      </p>
                    </div>
                  </div>

                  {/* Status Pill Badge */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold ${order.status === constant.statuses.APPROVE
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : order.status === constant.statuses.PAID
                          ? "bg-sky-50 text-sky-700 border border-sky-200"
                          : "bg-rose-50 text-rose-700 border border-rose-200"
                        }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${order.status === constant.statuses.APPROVE
                          ? "bg-emerald-500"
                          : order.status === constant.statuses.PAID
                            ? "bg-sky-500"
                            : "bg-rose-500"
                          }`}
                      />
                      {constant.statusesTranslate[order.status] || order.status}
                    </span>
                  </div>
                </div>

                {/* Order Fields Grid (3 Columns) */}
                <div className="p-5 grid grid-cols-3 gap-4">
                  {/* 1. Status Dropdown */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">สถานะคำสั่งซื้อ</label>
                    <select
                      disabled={loading}
                      value={order?.status || ""}
                      onChange={(e) => handleChangeOrderStatus(e.target.value)}
                      className="h-9 px-3 text-xs font-semibold rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue"
                    >
                      <option value={constant.statuses.APPROVE}>{constant.statusesTranslate.approved}</option>
                      <option disabled value={constant.statuses.PAID}>{constant.statusesTranslate.paid}</option>
                      <option value={constant.statuses.CANCEL}>{constant.statusesTranslate.canceled}</option>
                    </select>
                  </div>

                  {/* 2. Coupon Name */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">ชื่อคูปอง</label>
                    <div className="h-9 px-3 flex items-center text-xs font-semibold rounded-md bg-slate-50 border border-slate-200 text-slate-700 truncate">
                      {order?.coupon?.name || "-"}
                    </div>
                  </div>

                  {/* 3. Coupon Type */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">ประเภทคูปอง</label>
                    <div className="h-9 px-3 flex items-center text-xs font-semibold rounded-md bg-slate-50 border border-slate-200 text-slate-700">
                      {order?.coupon?.type === "reserve" ? "สิทธิ์จองลอตเตอรี่" : order?.coupon?.type || "-"}
                    </div>
                  </div>

                  {/* 4. Price & Inline Edit */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">
                      ราคา ({order.coupon?.isPointRedeem ? "คะแนน" : "บาท"})
                    </label>
                    {editingPrice ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          className="h-9 w-full px-3 text-xs font-semibold rounded-md border border-blue bg-white focus:outline-none"
                          value={newPrice}
                          onChange={(e) => setNewPrice(e.target.value)}
                          autoFocus
                        />
                        <button
                          onClick={handleSavePrice}
                          className="h-9 px-2.5 bg-blue text-white rounded-md text-xs font-bold hover:opacity-90"
                        >
                          บันทึก
                        </button>
                        <button
                          onClick={() => setEditingPrice(false)}
                          className="h-9 px-2 text-slate-500 hover:text-slate-700"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="h-9 px-3 flex items-center justify-between text-xs font-bold rounded-md bg-slate-50 border border-slate-200 text-slate-800">
                        <span>{(order?.price || 0).toLocaleString()} {order?.coupon?.isPointRedeem ? "แต้ม" : "฿"}</span>
                        <button
                          onClick={() => {
                            setNewPrice(String(order?.price || 0));
                            setEditingPrice(true);
                          }}
                          className="text-blue hover:underline text-[11px] font-semibold flex items-center gap-0.5"
                        >
                          <RiEditLine /> แก้ไข
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 5. Total Quantity */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">จำนวนสิทธิ์ทั้งหมด</label>
                    <div className="h-9 px-3 flex items-center text-xs font-bold rounded-md bg-slate-50 border border-slate-200 text-slate-700">
                      {order?.coupon?.quantity || 0} สิทธิ์ ({order?.coupon?.limitPerUsed || 0} ใบ/สิทธิ์)
                    </div>
                  </div>

                  {/* 6. Coupon Status */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">สถานะคูปอง</label>
                    <select
                      disabled={loading}
                      value={order?.coupon?.status || ""}
                      onChange={(e) => handleChangeCouponStatus(e.target.value)}
                      className="h-9 px-3 text-xs font-semibold rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue"
                    >
                      <option value="active">พร้อมใช้งาน (active)</option>
                      <option value="used">ใช้สิทธิ์แล้ว (used)</option>
                      <option value="expired">หมดอายุ (expired)</option>
                      <option value="canceled">ยกเลิก (canceled)</option>
                    </select>
                  </div>

                  {/* 7. Expire Date */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">วันหมดอายุคูปอง</label>
                    {editingExpire ? (
                      <div className="flex items-center gap-1.5">
                        <DatePicker
                          showTime={{ format: "HH:mm" }}
                          format="DD/MM/YYYY HH:mm"
                          locale={th}
                          value={newExpireAt}
                          onChange={(val) => setNewExpireAt(val)}
                          className="h-9 w-full text-xs font-semibold rounded-md border border-blue"
                          placeholder="เลือกวันหมดอายุ..."
                          size="small"
                        />
                        <button
                          onClick={handleSaveExpireAt}
                          disabled={loading || !newExpireAt}
                          className="h-9 px-3 bg-emerald-600 text-white text-xs font-bold rounded-md hover:bg-emerald-700 transition shrink-0"
                        >
                          บันทึก
                        </button>
                        <button
                          onClick={() => setEditingExpire(false)}
                          className="h-9 px-2 bg-slate-100 text-slate-500 text-xs font-bold rounded-md hover:bg-slate-200 transition shrink-0"
                        >
                          <RiCloseLine className="text-base" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-9 px-3 flex items-center justify-between text-xs font-semibold rounded-md bg-slate-50 border border-slate-200 text-slate-700">
                        <span>
                          {order?.coupon?.expireAt ? dayjs(order.coupon.expireAt).format("DD/MM/YYYY HH:mm") : "-"}
                        </span>
                        <button
                          onClick={() => {
                            setNewExpireAt(order?.coupon?.expireAt ? dayjs(order.coupon.expireAt) : dayjs());
                            setEditingExpire(true);
                          }}
                          className="text-xs text-blue hover:underline flex items-center gap-1 font-semibold shrink-0"
                        >
                          <RiEditLine /> แก้ไข
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 8. Approved By */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600">ผู้อนุมัติ</label>
                    <div className="h-9 px-3 flex items-center text-xs font-semibold rounded-md bg-slate-50 border border-slate-200 text-slate-700">
                      {order?.approvedBy || "-"}
                    </div>
                  </div>

                  {/* 9. Used In Order (if any) */}
                  {order?.coupon?.usedBy ? (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-slate-600">คำสั่งซื้อที่ใช้คูปอง</label>
                      <button
                        onClick={() => window.open(`/orders/${order?.coupon?.usedBy}`, "_blank")}
                        className="h-9 px-3 flex items-center justify-between text-xs font-bold rounded-md bg-blue/10 border border-blue/20 text-blue hover:bg-blue/15 transition"
                      >
                        <span>#{order?.coupon?.usedBy.slice(-6).toUpperCase()}</span>
                        <FiExternalLink />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-slate-600">รหัสคำสั่งซื้อสลาก</label>
                      <div className="h-9 px-3 flex items-center text-xs font-semibold rounded-md bg-slate-50 border border-slate-200 text-slate-400">
                        ยังไม่ได้นำไปใช้
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Card 2: Reserve Rights Table & Auto-Match */}
              {order.coupon?.type === constant.couponType.RESERVE && (
                <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
                  {/* Table Header & Hero Action Controls */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/60">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-md bg-amber-500/10 flex items-center justify-center text-amber-600 font-bold">
                        <RiTicketLine className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                          รายการจองเลขท้าย
                          <span className="px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-700 text-xs font-bold">
                            {order.coupon?.reserveOrder?.length || 0} / {order.coupon?.quantity || 0} สิทธิ์
                          </span>
                        </h3>
                        <p className="text-xs text-slate-500 font-medium">
                          ประเภทเลข: {order.coupon?.reserveType === "lastThree" ? "เลขท้าย 3 ตัว" : "เลขท้าย 2 ตัว"} • จำกัด {order.coupon?.limitPerUsed} ใบต่อสิทธิ์
                        </p>
                      </div>
                    </div>

                    {/* Actions Header */}
                    <div className="flex items-center gap-2.5">
                      {/* Auto-Match All Button */}
                      {pendingReserves.length > 0 && (
                        <button
                          disabled={autoMatchLoading || order.status !== "approved"}
                          onClick={() => handleAutoMatch(null)}
                          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#003A9E] hover:bg-[#002f80] text-white text-xs font-bold rounded-md shadow-sm transition disabled:opacity-50"
                        >
                          {autoMatchLoading ? (
                            <Spinner className="w-3.5 h-3.5 text-white" />
                          ) : (
                            <RiFlashlightLine className="w-3.5 h-3.5 text-amber-300" />
                          )}
                          <span>⚡ จัดสลากอัตโนมัติทั้งหมด ({pendingReserves.length})</span>
                        </button>
                      )}

                      {/* Add Reserve Slot Button */}
                      {order.coupon?.quantity > (order.coupon?.reserveOrder?.length || 0) &&
                        order.coupon.status === statuses.ACTIVE && (
                          <button
                            onClick={() =>
                              setReserveOrder({
                                number: "",
                                quantity: order.coupon?.limitPerUsed || "",
                              })
                            }
                            className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 text-xs font-bold rounded-md shadow-2xs transition"
                          >
                            <FaPlus className="w-2.5 h-2.5 text-blue" />
                            <span>เพิ่มสิทธิ์จอง</span>
                          </button>
                        )}
                    </div>
                  </div>

                  {/* Reserve Orders Table */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-slate-50 border-b border-slate-200">
                        <TableRow>
                          <TableHead className="w-16 text-center text-xs font-bold text-slate-600">#</TableHead>
                          <TableHead className="text-center text-xs font-bold text-slate-600">เลขท้ายที่จอง</TableHead>
                          <TableHead className="text-center text-xs font-bold text-slate-600">จำนวนสลาก</TableHead>
                          <TableHead className="text-center text-xs font-bold text-slate-600">สถานะ</TableHead>
                          <TableHead className="text-right text-xs font-bold text-slate-600 pr-6">การจัดการ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {order.coupon?.reserveOrder?.length > 0 ? (
                          order.coupon.reserveOrder.map((resOrder, idx) => {
                            const isPending = resOrder.status === statuses.PENDING || !resOrder.status;
                            return (
                              <TableRow key={idx} className="hover:bg-slate-50/70 transition">
                                <TableCell className="text-center text-xs font-bold text-slate-500">
                                  {idx + 1}
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="inline-block px-3 py-1 rounded font-mono font-bold text-sm tracking-widest bg-slate-100 text-slate-800 border border-slate-200">
                                    {resOrder.number}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center text-xs font-bold text-slate-700">
                                  {resOrder.quantity} ใบ
                                </TableCell>
                                <TableCell className="text-center">
                                  {isPending ? (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                      รอดำเนินการ
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                      จัดสลากแล้ว
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="pr-6">
                                  <div className="flex items-center justify-end gap-2">
                                    {isPending ? (
                                      <>
                                        {/* Quick Auto-Match Row */}
                                        <button
                                          disabled={autoMatchLoading || order.status !== "approved"}
                                          onClick={() => handleAutoMatch(resOrder.number)}
                                          title="จัดสลากอัตโนมัติเฉพาะเลขนี้"
                                          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold bg-blue/10 text-blue hover:bg-blue/20 transition disabled:opacity-40"
                                        >
                                          <RiFlashlightLine className="w-3.5 h-3.5" />
                                          <span>จัดอัตโนมัติ</span>
                                        </button>

                                        {/* Manual Picker Button */}
                                        <button
                                          disabled={order.status !== "approved"}
                                          onClick={() => setSelectReserve(resOrder)}
                                          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                                        >
                                          <FiSearch className="w-3 h-3" />
                                          <span>เลือกเอง</span>
                                        </button>

                                        {/* Delete Button */}
                                        <button
                                          disabled={order.status !== "approved"}
                                          onClick={() => handleRemoveReserveOrder(resOrder)}
                                          title="ลบสิทธิ์จอง"
                                          className="p-2 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                                        >
                                          <IoMdTrash className="w-4 h-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        {/* View Fulfilled Order */}
                                        {resOrder.orderId && (
                                          <button
                                            onClick={() => window.open(`/orders/${resOrder.orderId}`, "_blank")}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition shadow-2xs"
                                          >
                                            <span>คำสั่งซื้อ #{resOrder.shortId || resOrder.orderId?.slice(-5).toUpperCase()}</span>
                                            <span className="text-[11px] font-normal">({resOrder.size || resOrder.quantity} ใบ)</span>
                                            <FiExternalLink className="w-3 h-3" />
                                          </button>
                                        )}

                                        {/* Option 3: Delete Button for Used Reserve Orders */}
                                        <button
                                          onClick={() =>
                                            setDeleteUsedModal({
                                              open: true,
                                              resOrder,
                                              cancelOrder: Boolean(resOrder.orderId),
                                            })
                                          }
                                          title="ลบสิทธิ์จองที่จัดแล้ว"
                                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition text-xs font-bold shadow-2xs cursor-pointer"
                                        >
                                          <IoMdTrash className="w-3.5 h-3.5" />
                                          <span className="text-[10px]">ลบสิทธิ์</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} className="py-12 text-center text-slate-400 font-semibold text-xs">
                              ยังไม่มีรายการจองเลขท้ายสำหรับคูปองนี้
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Add Reserve Order Inline Form */}
                        {reserveOrder && (
                          <TableRow className="bg-blue/5 border-t border-blue/20">
                            <TableCell className="text-center text-xs font-bold text-blue">
                              +
                            </TableCell>
                            <TableCell className="text-center">
                              <input
                                type="text"
                                maxLength={order?.coupon?.reserveType === "lastThree" ? 3 : 2}
                                value={reserveOrder?.number || ""}
                                placeholder={order?.coupon?.reserveType === "lastThree" ? "123" : "12"}
                                onChange={(e) => {
                                  const maxLen = order?.coupon?.reserveType === "lastThree" ? 3 : 2;
                                  if (e.target.value.length <= maxLen) {
                                    setReserveOrder((prev) => ({ ...prev, number: e.target.value }));
                                  }
                                }}
                                className="w-24 h-8 text-center font-mono font-bold text-sm tracking-widest border border-blue rounded-md bg-white focus:outline-none"
                                autoFocus
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <input
                                type="number"
                                value={reserveOrder?.quantity || ""}
                                placeholder={String(order?.coupon?.limitPerUsed || 1)}
                                onChange={(e) => {
                                  if (Number(e.target.value) <= (order?.coupon?.limitPerUsed || 100)) {
                                    setReserveOrder((prev) => ({ ...prev, quantity: e.target.value }));
                                  }
                                }}
                                className="w-20 h-8 text-center text-xs font-bold border border-blue rounded-md bg-white focus:outline-none"
                              />
                            </TableCell>
                            <TableCell className="text-center text-xs font-semibold text-slate-500">
                              ใหม่
                            </TableCell>
                            <TableCell className="pr-6">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  disabled={
                                    !reserveOrder?.quantity ||
                                    !reserveOrder?.number ||
                                    reserveOrder.number.length !==
                                    (order?.coupon?.reserveType === "lastThree" ? 3 : 2)
                                  }
                                  onClick={async () => {
                                    setLoading(true);
                                    const { status } = await callApi(
                                      "coupons-addReserveOrder",
                                      {
                                        id: order.id,
                                        number: reserveOrder?.number,
                                        quantity: reserveOrder?.quantity,
                                      },
                                      { notShowLoading: true }
                                    );
                                    if (status) {
                                      toast({
                                        className: "bg-emerald-600 text-white font-semibold",
                                        title: (
                                          <div className="flex gap-2 items-center">
                                            <FaCheckCircle /> เพิ่มสิทธิ์จองสำเร็จ
                                          </div>
                                        ),
                                      });
                                      getOrder();
                                      setReserveOrder(false);
                                    } else {
                                      toast({
                                        className: "bg-[#c15b78] text-white font-semibold",
                                        title: (
                                          <div className="flex gap-2 items-center">
                                            <FaTimes /> เพิ่มสิทธิ์จองไม่สำเร็จ
                                          </div>
                                        ),
                                      });
                                    }
                                    setLoading(false);
                                  }}
                                  className="px-3.5 py-1.5 bg-blue text-white rounded-md text-xs font-bold hover:opacity-90 transition disabled:opacity-40"
                                >
                                  บันทึก
                                </button>
                                <button
                                  onClick={() => setReserveOrder(false)}
                                  className="px-2.5 py-1.5 text-slate-500 hover:text-slate-700 text-xs font-semibold"
                                >
                                  ยกเลิก
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Section 2.1: Manual Reserve Order Creator (when a row is selected) */}
                  {selectReserve && selectReserve.status === statuses.PENDING && (
                    <div className="border-t-2 border-blue bg-white transition-all">
                      <div className="flex items-center justify-between px-5 py-3.5 bg-blue text-white">
                        <div className="flex items-center gap-2">
                          <RiFlashlightLine className="w-5 h-5 text-amber-300" />
                          <div>
                            <h4 className="text-sm font-bold">
                              จัดสลากด้วยตนเอง: เลขท้าย {selectReserve.number}
                            </h4>
                            <p className="text-[11px] text-blue-100">
                              ต้องการจำนวน {selectReserve.quantity} ใบ
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectReserve(false)}
                          className="p-1 rounded-md hover:bg-white/20 text-white transition"
                        >
                          <RiCloseLine className="w-5 h-5" />
                        </button>
                      </div>

                      <CreateReserveOrder
                        user={user}
                        order={order}
                        selectReserve={selectReserve}
                        setSelectReserve={setSelectReserve}
                        orderId={order.id}
                        getOrder={getOrder}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* ───────────────────────────────────────────────────────────── */}
            {/* RIGHT COLUMN (Col 4): Slip (Top) & Notes (Bottom)              */}
            {/* ───────────────────────────────────────────────────────────── */}
            <div className="col-span-12 lg:col-span-4 flex flex-col gap-5">
              {/* Card 1: Slip Preview */}
              <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                <div className="px-4 py-3.5 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-700">สลิปการโอนเงิน</h3>
                  {order.slip && (
                    <a
                      href={order.slip}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] font-semibold text-blue hover:underline flex items-center gap-1"
                    >
                      <span>ดูรูปขยาย</span>
                      <FiExternalLink />
                    </a>
                  )}
                </div>
                <div className="p-3 flex flex-1 flex-col items-center justify-center min-h-[190px] bg-slate-50/30">
                  {order.slip ? (
                    <a href={order.slip} target="_blank" rel="noreferrer" className="group relative block w-full">
                      <img
                        src={order.slip}
                        alt="สลิปการโอนเงิน"
                        className="w-full h-auto object-contain mx-auto rounded-md border border-slate-200 shadow-2xs group-hover:opacity-90 transition"
                      />
                    </a>
                  ) : (
                    <div className="flex flex-col items-center gap-1.5 text-slate-400 py-8">
                      <RiCoupon2Line className="w-8 h-8 stroke-1 text-slate-300" />
                      <p className="text-xs font-semibold">
                        {order.coupon?.isPointRedeem ? "แลกด้วยคะแนน (ไม่มีสลิป)" : "ไม่พบสลิปการโอน"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Card 2: Notes & Activity Timeline */}
              <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                <div className="px-4 py-3.5 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    บันทึกช่วยจำ (Notes)
                  </h3>
                  <span className="text-[11px] text-slate-500 font-semibold">
                    {[...(order.notes || []), ...(order.note || [])]?.length || 0} รายการ
                  </span>
                </div>

                {/* Add Note Form */}
                <div className="p-3 border-b border-slate-100 bg-white">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddNote();
                      }}
                      placeholder="พิมพ์ข้อความบันทึก..."
                      className="h-9 w-full px-3 text-xs font-medium rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-blue"
                    />
                    <button
                      disabled={noteLoading || !note.trim()}
                      onClick={handleAddNote}
                      className="h-9 px-3.5 bg-blue text-white rounded-md text-xs font-bold hover:opacity-90 transition disabled:opacity-40 shrink-0"
                    >
                      {noteLoading ? <Spinner className="w-3.5 h-3.5 text-white" /> : "เพิ่ม"}
                    </button>
                  </div>
                </div>

                {/* Notes List */}
                <ScrollArea className="h-64 p-3">
                  {[...(order.notes || []), ...(order.note || [])]?.length > 0 ? (
                    <div className="flex flex-col gap-2.5">
                      {[...(order.notes || []), ...(order.note || [])]
                        .sort((a, b) => (b?.time || 0) - (a?.time || 0))
                        .map((item, idx) => (
                          <div
                            key={idx}
                            className="p-2.5 rounded-md bg-slate-50 border border-slate-200/80 flex flex-col gap-1"
                          >
                            <p className="text-xs font-medium text-slate-800 break-words">
                              {item.note}
                            </p>
                            <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-200/50">
                              <span className="font-bold text-slate-700">{item.by || "Admin"}</span>
                              <span className="text-slate-400 font-medium">
                                {item.time
                                  ? new Date(item.time).toLocaleDateString("th-TH", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                  : "-"}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="h-44 flex flex-col items-center justify-center text-slate-400 text-xs font-semibold gap-1">
                      <RiTimeLine className="w-6 h-6 stroke-1 text-slate-300" />
                      <p>ยังไม่มีบันทึกช่วยจำ</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>

          {/* ═════════════════════════════════════════════════════════════════ */}
          {/* BOTTOM SECTION: ข้อมูลลูกค้า (UserPreview) - FULL WIDTH (Col 12)  */}
          {/* ═════════════════════════════════════════════════════════════════ */}
          {profileState.role !== "scanner" && user && (
            <div className="w-full mt-1">
              <UserPreview user={user} />
            </div>
          )}
        </div>
      )}


      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* Option 3 Modal: Delete Used Reserve Order with Cancel Checkbox   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <AlertDialog
        open={deleteUsedModal.open}
        onOpenChange={(isOpen) =>
          setDeleteUsedModal((prev) => ({ ...prev, open: isOpen }))
        }
      >
        <AlertDialogContent className="rounded-md border border-slate-200 max-w-[460px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
              <span className="text-rose-600">⚠️</span>
              <span>ยืนยันการลบสิทธิ์จองที่จัดสลากแล้ว</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-slate-600 font-medium mt-1.5 leading-relaxed">
              ต้องการลบรายการจองเลขท้าย{" "}
              <b className="font-mono text-slate-800">
                {deleteUsedModal.resOrder?.number}
              </b>{" "}
              ({deleteUsedModal.resOrder?.quantity} ใบ) ออกจากคูปองใช่หรือไม่?
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Option 3 Checkbox: Cancel associated order & return tickets */}
          {deleteUsedModal.resOrder?.orderId && (
            <label className="flex items-start gap-3 p-3.5 rounded-md bg-amber-50/80 border border-amber-200 cursor-pointer mt-3 transition hover:bg-amber-50">
              <input
                type="checkbox"
                checked={deleteUsedModal.cancelOrder}
                onChange={(e) =>
                  setDeleteUsedModal((prev) => ({
                    ...prev,
                    cancelOrder: e.target.checked,
                  }))
                }
                className="mt-0.5 w-4 h-4 rounded text-rose-600 focus:ring-rose-500 cursor-pointer"
              />
              <div className="text-xs text-slate-700">
                <p className="font-bold text-slate-800">
                  ยกเลิกคำสั่งซื้อ #{deleteUsedModal.resOrder?.shortId || deleteUsedModal.resOrder?.orderId?.slice(-5).toUpperCase()} และคืนสลากกลับคลัง
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  สลากในออเดอร์นี้จะถูกเปลี่ยนสถานะกลับเป็นพร้อมขาย (Unsold) เพื่อให้นำมาเทสใหม่ได้ทันที
                </p>
              </div>
            </label>
          )}

          <AlertDialogFooter className="mt-5 gap-2">
            <AlertDialogCancel className="h-9 px-4 text-xs font-semibold text-slate-600 rounded-md border border-slate-200">
              ยกเลิก
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecuteDeleteUsed}
              className="h-9 px-4 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-md transition"
            >
              ยืนยันลบสิทธิ์ (DEV)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Component: CreateReserveOrder (Manual Lottery Picker & Cart Config)
// ═════════════════════════════════════════════════════════════════════════════
const CreateReserveOrder = ({
  user = {},
  order = {},
  selectReserve,
  setSelectReserve,
  orderId,
  getOrder,
}) => {
  const { toast } = useToast();
  const { callApi, configs } = Context();
  const [lottoNumber, setLottoNumber] = useState(selectReserve.number || "");
  const [lottos, setLottos] = useState(false);
  const [type, setType] = useState(
    selectReserve.number?.split("")?.length === 3 ? "lastThree" : "lastTwo"
  );
  const [cart, setCart] = useState([]);
  const [cartMap, setCartMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [stockFilter, setStockFilter] = useState("all");

  const [orderBody, setOrderBody] = useState({
    firstName: user?.firstName || order?.userData?.name?.split(/\s+/)[0] || "",
    lastName: user?.lastName || order?.userData?.name?.split(/\s+/).slice(1).join(" ") || "",
    phone: user?.phone || order?.userData?.phone || "",
    deliverChannel: "online",
    userId: user?.userId || order?.userId || order?.userData?.userId || "",
    userNo: user?.userNo || order?.userNo || order?.userData?.userNo || "",
    lineId: user?.lineId || order?.userData?.lineId || "",
    bank: {
      name: user?.bank?.name || "",
      accNo: user?.bank?.accNo || "",
      accName: user?.bank?.accName || "",
    },
    status: statuses.PENDING,
    expire: configs.expireOrder,
    totalPrice: 0,
  });

  useEffect(() => {
    if (user || order) {
      const uData = order?.userData || {};
      const nameParts = (uData.name || "").trim().split(/\s+/);
      setOrderBody((prev) => ({
        ...prev,
        firstName: user?.firstName || prev.firstName || nameParts[0] || "",
        lastName: user?.lastName || prev.lastName || nameParts.slice(1).join(" ") || "",
        phone: user?.phone || prev.phone || uData.phone || "",
        userId: user?.userId || prev.userId || order?.userId || uData.userId || "",
        userNo: user?.userNo || prev.userNo || order?.userNo || uData.userNo || "",
        lineId: user?.lineId || prev.lineId || uData.lineId || "",
      }));
    }
  }, [user, order]);

  const searchLotto = async () => {
    setLottos(false);
    setLoading(true);
    let queries = [
      ["isSold", false],
      ["roundDate", configs.roundDate],
    ];

    if (stockFilter === "backoffice") {
      queries.push(["isOnBlock", false]);
    } else if (stockFilter === "block") {
      queries.push(["isOnBlock", true]);
    }

    if (lottoNumber !== "") {
      switch (type) {
        case "lastTwo":
          queries.push(["lastTwo", lottoNumber]);
          break;
        case "lastThree":
          queries.push(["lastThree", lottoNumber]);
          break;
        case "firstThree":
          queries.push(["firstThree", lottoNumber]);
          break;
        case "number":
          queries.push(["number", lottoNumber]);
          break;
        case "fileNameId":
          queries.push(["fileNameId", lottoNumber]);
          break;
        default:
          break;
      }
    }

    const blockResults = await callApi(
      "lotteries-getAll",
      {
        queries,
        limit: 200,
        roundDate: configs.roundDate,
      },
      { notShowLoading: true }
    );
    setLoading(false);
    setLottos(blockResults || []);
  };

  const targetQuantity = selectReserve.quantity || 1;
  const isCartFull = cart.length >= targetQuantity;

  return (
    <div className="p-5 flex flex-col gap-5">
      {/* 2-Columns: Search & Results (Left) | Basket & Delivery (Right) */}
      <div className="grid grid-cols-12 gap-5 items-start">
        {/* LEFT SUB-COL (7): Lottery Search & Results */}
        <div className="col-span-7 flex flex-col gap-4 border-r border-slate-200 pr-5">
          <div className="flex items-center justify-between">
            <h5 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              1. ค้นหาสลากในคลัง
            </h5>
            <span className="text-[11px] text-slate-500 font-medium">
              งวด: {configs.roundDate}
            </span>
          </div>

          {/* Search Inputs */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-28">
              <input
                className="h-9 w-full border border-slate-200 px-3 text-xs font-mono font-bold tracking-wider rounded-md focus:outline-none focus:ring-1 focus:ring-blue"
                type="text"
                placeholder="เลขสลาก"
                value={lottoNumber}
                onChange={(e) => setLottoNumber(e.target.value)}
              />
            </div>
            <div className="w-32">
              <select
                className="h-9 w-full border border-slate-200 px-2 text-xs font-semibold rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="lastTwo">เลขท้าย 2 ตัว</option>
                <option value="lastThree">เลขท้าย 3 ตัว</option>
                <option value="firstThree">เลขหน้า 3 ตัว</option>
                <option value="number">เลข 6 หลัก</option>
                <option value="fileNameId">ชื่อเล่ม</option>
              </select>
            </div>
            <div className="w-32">
              <select
                className="h-9 w-full border border-slate-200 px-2 text-xs font-semibold rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue"
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
              >
                <option value="all">สต็อก: ทั้งหมด</option>
                <option value="backoffice">เฉพาะหลังบ้าน</option>
                <option value="block">เฉพาะหน้าบ้าน</option>
              </select>
            </div>
            <button
              disabled={loading}
              onClick={searchLotto}
              className="h-9 px-4 bg-blue hover:bg-blue/90 text-white rounded-md text-xs font-bold transition flex items-center gap-1.5"
            >
              {loading ? <Spinner className="w-3.5 h-3.5 text-white" /> : <FiSearch />}
              <span>ค้นหา</span>
            </button>
          </div>

          {/* Results Table */}
          {lottos && (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs">
                <span className="font-bold text-slate-700">
                  ผลการค้นหา ({lottos.length} ใบ)
                </span>
                {lottos.length > 0 && (
                  <button
                    disabled={isCartFull}
                    onClick={() => {
                      const needed = targetQuantity - cart.length;
                      if (needed <= 0) return;
                      const availableLottos = lottos
                        .filter((l) => !cart.includes(l.id))
                        .slice(0, needed);
                      const availableToAdd = availableLottos.map((l) => l.id);
                      setCart((prev) => [...prev, ...availableToAdd]);
                      setCartMap((prev) => {
                        const updated = { ...prev };
                        availableLottos.forEach((l) => {
                          updated[l.id] = l;
                        });
                        return updated;
                      });
                    }}
                    className="text-[11px] font-bold text-blue hover:underline disabled:opacity-40"
                  >
                    + ใส่ตะกร้าตามจำนวนที่ขาด
                  </button>
                )}
              </div>

              <ScrollArea className="h-64">
                {lottos.length > 0 ? (
                  <Table>
                    <TableHeader className="bg-slate-50/50">
                      <TableRow>
                        <TableHead className="text-center text-xs font-bold">เลขสลาก</TableHead>
                        <TableHead className="text-center text-xs font-bold">สต็อก</TableHead>
                        <TableHead className="text-center text-xs font-bold">เล่ม</TableHead>
                        <TableHead className="text-right text-xs font-bold pr-3">ใส่ตะกร้า</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lottos.map((lotto) => {
                        const inCart = cart.includes(lotto.id);
                        return (
                          <TableRow key={lotto.id} className="hover:bg-slate-50/50">
                            <TableCell className="text-center font-mono font-bold text-sm text-slate-800">
                              {lotto.id}
                            </TableCell>
                            <TableCell className="text-center">
                              {lotto.isOnBlock ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                  หน้าบ้าน
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue/10 text-blue border border-blue/20">
                                  หลังบ้าน
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center text-xs font-medium text-slate-500">
                              {lotto.fileNameId}
                            </TableCell>
                            <TableCell className="text-right pr-3">
                              <button
                                disabled={inCart || isCartFull}
                                onClick={() => {
                                  setCart((prev) => [...prev, lotto.id]);
                                  setCartMap((prev) => ({ ...prev, [lotto.id]: lotto }));
                                }}
                                className={`px-2.5 py-1 rounded text-xs font-bold transition ${inCart
                                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                  : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                                  }`}
                              >
                                {inCart ? "อยู่ในตะกร้า" : "+ เลือก"}
                              </button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="h-40 flex items-center justify-center text-xs text-slate-400 font-semibold">
                    ไม่พบสลากที่ตรงกับเงื่อนไข
                  </div>
                )}
              </ScrollArea>
            </div>
          )}
        </div>

        {/* RIGHT SUB-COL (5): Cart & Delivery Info */}
        <div className="col-span-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h5 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              2. สลากในตะกร้า
            </h5>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-bold ${cart.length === targetQuantity
                ? "bg-emerald-100 text-emerald-800"
                : "bg-amber-100 text-amber-800"
                }`}
            >
              เลือกแล้ว {cart.length} / {targetQuantity} ใบ
            </span>
          </div>

          {/* Cart Items List */}
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <ScrollArea className="h-48">
              {cart.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {cart.map((ticketId, idx) => {
                    const lottoItem = cartMap[ticketId];
                    return (
                      <div
                        key={ticketId}
                        className="px-3 py-2 flex items-center justify-between text-xs hover:bg-slate-50 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 font-bold">{idx + 1}.</span>
                          <span className="font-mono font-bold text-sm text-slate-800 tracking-wider">
                            {ticketId}
                          </span>
                          {lottoItem && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                lottoItem.isOnBlock
                                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                                  : "bg-blue/10 text-blue border border-blue/20"
                              }`}
                            >
                              {lottoItem.isOnBlock ? "หน้าบ้าน" : "หลังบ้าน"}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setCart((prev) => prev.filter((id) => id !== ticketId));
                            setCartMap((prev) => {
                              const updated = { ...prev };
                              delete updated[ticketId];
                              return updated;
                            });
                          }}
                          className="p-1 text-slate-400 hover:text-rose-600 rounded transition"
                        >
                          <MdDeleteOutline className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-44 flex flex-col items-center justify-center text-xs text-slate-400 font-semibold gap-1">
                  <RiTicketLine className="w-6 h-6 stroke-1" />
                  <p>ยังไม่มีสลากในตะกร้า</p>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Recipient & Channel */}
          <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
            <label className="text-xs font-bold text-slate-600">ช่องทางการจัดส่ง</label>
            <select
              className="h-9 px-3 text-xs font-semibold rounded-md border border-slate-200 bg-white"
              value={orderBody.deliverChannel}
              onChange={(e) => setOrderBody((prev) => ({ ...prev, deliverChannel: e.target.value }))}
            >
              <option value="online">เช่าพื้นที่พร้อมบริการฯ (ฝากเก็บ)</option>
              <option value="mail">ส่งด่วน EMS (จัดส่งถึงบ้าน)</option>
              <option value="offline">ลอตเตอรี่ 80 บาท</option>
            </select>
          </div>

          {/* Submit Action Button */}
          <button
            disabled={loading || cart.length !== targetQuantity}
            onClick={async () => {
              setLoading(true);
              const targetUserId =
                orderBody.userId ||
                user?.userId ||
                order?.userId ||
                order?.userData?.userId;
              const { status, message } = await callApi(
                "orders-createReserveOrder",
                {
                  lotteryIds: cart,
                  userId: targetUserId,
                  couponOrderId: orderId,
                  selectReserve: selectReserve,
                  firstName: orderBody.firstName,
                  lastName: orderBody.lastName,
                  phone: orderBody.phone,
                  deliverChannel: orderBody.deliverChannel,
                },
                { notShowLoading: true }
              );
              if (status) {
                toast({
                  className: "bg-emerald-600 text-white font-semibold",
                  title: (
                    <div className="flex gap-2 items-center">
                      <FaCheckCircle /> สร้างคำสั่งซื้อสิทธิ์จองสำเร็จ
                    </div>
                  ),
                });
                getOrder();
                setSelectReserve(false);
              } else {
                toast({
                  className: "bg-[#c15b78] text-white font-semibold",
                  title: (
                    <div className="flex gap-2 items-center">
                      <FaTimes /> {message || "ไม่สามารถสร้างคำสั่งซื้อได้"}
                    </div>
                  ),
                });
              }
              setLoading(false);
            }}
            className="h-10 mt-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-md shadow-sm transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:hover:bg-emerald-600"
          >
            {loading ? (
              <Spinner className="w-4 h-4 text-white" />
            ) : (
              <>
                <FaCheckCircle />
                <span>ยืนยันสร้างคำสั่งซื้อ ({cart.length}/{targetQuantity} ใบ)</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
